/**
 * Analytics Service - GA4 + Custom Analytics Integration
 * 
 * @description
 * Comprehensive analytics solution combining Google Analytics 4 for web metrics
 * and custom analytics for QuickLinks-specific tracking.
 * 
 * @features
 * - Google Analytics 4 integration
 * - Custom event tracking with backend integration
 * - GDPR compliance with cookie consent
 * - User opt-out functionality
 * - Graceful degradation when backend unavailable
 * 
 * @privacy
 * - Cookie consent management
 * - User opt-out support
 * - Anonymous tracking options
 * - Data retention controls
 */

// Import API service functions for backend communication
import { 
  sendAnalyticsEvent, 
  trackEvent, 
  trackLinkClick,
  batchSendAnalyticsEvents,
  getApiErrorMessage,
  getAnalytics,
  getAnalyticsSummary,
  getUserAnalytics,
  getAdminAnalytics
} from './api.js';

// Configuration
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || 'G-XXXXXXXXXX';

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.hasConsent = false;
    this.isOptedOut = false;
    this.customEvents = [];
    this.sessionId = this.generateSessionId();
    this.userId = null;
    
    // Connection tracking
    this._connectionFailureCount = 0;
    this._lastConnectionAttempt = 0;
    this._retryTimeout = null;
    this._isBackendAvailable = true;
    
    // Initialize on next tick to allow DOM to load
    setTimeout(() => this.init(), 0);
    
    // Set up periodic backend availability check
    setInterval(() => {
      if (!this._isBackendAvailable) {
        this.checkBackendAndRetryEvents();
      }
    }, 30000);
  }

  // Helper to get auth token consistently with API service
  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  /**
   * Initialize analytics service
   */
  async init() {
    if (this.isInitialized) return;

    try {
      // Check consent status
      this.hasConsent = this.getConsentStatus();
      this.isOptedOut = this.getOptOutStatus();

      // Initialize GA4 if consent given and not opted out
      if (this.hasConsent && !this.isOptedOut) {
        await this.initializeGA4();
      }

      // Initialize custom analytics (always - handles consent internally)
      this.initializeCustomAnalytics();

      this.isInitialized = true;
    } catch (error) {
      console.warn('Analytics initialization failed:', error.message);
    }
  }

  /**
   * Initialize Google Analytics 4
   */
  async initializeGA4() {
    try {
      // Load gtag script
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
      script.async = true;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() { window.dataLayer.push(arguments); };
      
      window.gtag('js', new Date());
      window.gtag('config', GA4_MEASUREMENT_ID, {
        cookie_flags: 'SameSite=None;Secure',
        send_page_view: false // We'll send manually
      });

      // Send initial page view
      this.trackPageView();
    } catch (error) {
      console.warn('GA4 initialization failed:', error.message);
    }
  }

  /**
   * Initialize custom analytics tracking
   */
  initializeCustomAnalytics() {
    // Track page views
    this.trackPageView();
    
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('page_hidden', {
          path: window.location.pathname
        });
      } else {
        this.trackEvent('page_visible', {
          path: window.location.pathname
        });
      }
    });
  }

  /**
   * Get consent status from localStorage
   */
  getConsentStatus() {
    return localStorage.getItem('analytics_consent') === 'granted';
  }

  /**
   * Set consent status
   */
  setConsentStatus(hasConsent) {
    localStorage.setItem('analytics_consent', hasConsent ? 'granted' : 'denied');
    this.hasConsent = hasConsent;
    
    if (hasConsent && !this.isOptedOut) {
      this.initializeGA4();
    }
  }

  /**
   * Grant consent for analytics
   */
  grantConsent() {
    this.setConsentStatus(true);
    
    // Update GA4 consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
  }

  /**
   * Deny consent for analytics
   */
  denyConsent() {
    this.setConsentStatus(false);
  }

  /**
   * Get opt-out status
   */
  getOptOutStatus() {
    return localStorage.getItem('analytics_optout') === 'true';
  }

  /**
   * Set opt-out status
   */
  setOptOutStatus(isOptedOut) {
    localStorage.setItem('analytics_optout', isOptedOut.toString());
    this.isOptedOut = isOptedOut;
    
    if (isOptedOut) {
      this.disableAnalytics();
    }
  }

  /**
   * Disable all analytics tracking
   */
  disableAnalytics() {
    this.isOptedOut = true;
    
    // Disable GA4
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId) {
    this.userId = userId;
    
    // Update GA4 user ID
    if (window.gtag && this.hasConsent && !this.isOptedOut) {
      window.gtag('config', GA4_MEASUREMENT_ID, {
        user_id: userId
      });
    }
  }

  /**
   * Clear user ID
   */
  clearUserId() {
    this.userId = null;
  }

  /**
   * Get current session information
   */
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      userId: this.userId,
      hasConsent: this.hasConsent,
      isOptedOut: this.isOptedOut
    };
  }

  /**
   * Track page view
   */
  trackPageView(path = window.location.pathname) {
    if (this.isOptedOut) return;

    // GA4 tracking
    if (window.gtag && this.hasConsent) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: path
      });
    }

    // Custom tracking
    this.trackCustomEvent('page_view', {
      path,
      title: document.title,
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track custom events
   */
  trackEvent(eventName, properties = {}) {
    if (this.isOptedOut) return;

    // GA4 tracking
    if (window.gtag && this.hasConsent) {
      window.gtag('event', eventName, properties);
    }

    // Custom tracking
    this.trackCustomEvent(eventName, properties);
  }

  /**
   * Track custom events with backend integration
   */
  async trackCustomEvent(eventName, properties = {}) {
    if (this.isOptedOut) return;

    const event = {
      id: `event_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      name: eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
        user_id: this.userId,
        page: window.location.pathname,
        referrer: document.referrer,
        user_agent: navigator.userAgent
      }
    };

    // Add to queue for batch sending
    this.customEvents.push(event);

    // Try to send immediately if backend is available
    if (this._isBackendAvailable) {
      try {
        await this.sendCustomEvent(event);
      } catch (error) {
        // Event will remain in queue for retry
        this._isBackendAvailable = false;
        this._connectionFailureCount++;
      }
    }
  }

  /**
   * Send custom events to backend
   */
  async sendCustomEvent(event) {
    try {
      const eventData = {
        event_type: event.name,
        event_data: event.properties,
        session_id: this.sessionId,
        user_id: this.userId,
        consent_level: this.hasConsent ? 'analytics' : 'none'
      };

      await sendAnalyticsEvent(eventData);
      
      // Remove successfully sent event from queue
      this.customEvents = this.customEvents.filter(e => e.id !== event.id);
      
      // Reset failure count on success
      this._connectionFailureCount = 0;
      this._isBackendAvailable = true;
    } catch (error) {
      this._connectionFailureCount++;
      this._lastConnectionAttempt = Date.now();
      
      if (this._connectionFailureCount > 3) {
        this._isBackendAvailable = false;
        
        // Schedule retry
        if (!this._retryTimeout) {
          this._retryTimeout = setTimeout(() => {
            this._retryTimeout = null;
            this.batchSendEvents();
          }, 60000);
        }
      }
      
      throw error;
    }
  }

  /**
   * Batch send queued events
   */
  async batchSendEvents() {
    if (this.customEvents.length === 0) return;

    try {
      const events = this.customEvents.map(event => ({
        event_type: event.name,
        event_data: event.properties,
        session_id: this.sessionId,
        user_id: this.userId,
        consent_level: this.hasConsent ? 'analytics' : 'none'
      }));

      await batchSendAnalyticsEvents(events);
      
      // Clear sent events
      this.customEvents = [];
      this._connectionFailureCount = 0;
      this._isBackendAvailable = true;
    } catch (error) {
      // Clear events anyway to prevent infinite accumulation
      this.customEvents = [];
    }
  }

  /**
   * Enhanced link click tracking (for PublicProfile compatibility)
   */
  async enhancedTrackLinkClick(linkData) {
    return this.trackLinkClick(linkData.id, linkData);
  }

  /**
   * Track link clicks
   */
  async trackLinkClick(linkId, additionalData = {}) {
    if (this.isOptedOut) return;

    const clickData = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      user_id: this.userId,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      ...additionalData
    };

    try {
      await trackLinkClick(linkId, clickData);
    } catch (error) {
      // Silently fail if backend unavailable
      console.warn('Link click tracking failed:', error.message);
    }

    // Also track as generic event
    this.trackEvent('link_click', {
      link_id: linkId,
      ...clickData
    });
  }

  /**
   * Track tier upgrades
   */
  trackTierUpgrade(upgradeData) {
    this.trackEvent('tier_upgrade', upgradeData);
  }

  /**
   * Track user signups
   */
  trackSignup(signupData) {
    this.trackEvent('user_signup', signupData);
  }

  /**
   * Track profile views
   */
  trackProfileView(profileData) {
    this.trackEvent('profile_view', profileData);
  }

  /**
   * Get analytics data (gracefully handles backend unavailable)
   */
  async getAnalyticsData(days = 30) {
    try {
      return await getAnalytics(days);
    } catch (error) {
      console.warn('Analytics data unavailable:', error.message);
      return {
        totalViews: 0,
        uniqueVisitors: 0,
        linkClicks: 0,
        topPages: [],
        referrers: []
      };
    }
  }

  /**
   * Get analytics summary (fails if backend unavailable)
   */
  async getAnalyticsSummary(days = 30, isAdminContext = false) {
    return await getAnalyticsSummary(days, isAdminContext);
  }

  /**
   * Get admin analytics (fails if backend unavailable)
   */
  async getAdminAnalytics(days = 30, detailed = true) {
    return await getAdminAnalytics(days, detailed);
  }

  /**
   * Check backend availability and retry queued events
   */
  async checkBackendAndRetryEvents() {
    if (this.customEvents.length > 0) {
      await this.batchSendEvents();
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Export the service
export { analyticsService };
export default analyticsService;
